import React, { useRef, useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../lib/api';
import { SUPPORTED_LANGUAGES } from '../i18n';
import {
  LayoutDashboard,
  LogOut,
  Users,
  Cpu,
  Monitor,
  Building2,
  FileText,
  HardHat,
  Radio,
  ClipboardList,
  Receipt,
  Settings,
  Menu,
  X,
  KeyRound,
  Camera,
  Bell,
  Zap,
  Wrench,
  Calendar,
  Globe,
  ScrollText,
  Calculator,
  Smartphone,
} from 'lucide-react';
import RouteErrorBoundary from './RouteErrorBoundary';
import '../styles/layout.css';

// ---------------------------------------------------------------------------
// Starfield canvas (lightweight version for layout background)
// ---------------------------------------------------------------------------

type RGB = [number, number, number];

const THEME_COLORS: RGB[] = [
  [46, 229, 157],
  [122, 167, 255],
  [255, 209, 102],
  [234, 240, 255],
  [0, 244, 255],
];

const IS_MOBILE = typeof window !== 'undefined' && window.innerWidth <= 768;
const STAR_COUNT = IS_MOBILE ? 20 : 50;
const CONNECTION_DIST = IS_MOBILE ? 100 : 140;
const FPS_CAP = IS_MOBILE ? 16 : 24;
const FRAME_INTERVAL = 1000 / FPS_CAP;

interface Star {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  color: RGB;
  pulseSpeed: number;
  pulsePhase: number;
}

function createStar(w: number, h: number): Star {
  return {
    x: Math.random() * w,
    y: Math.random() * h,
    vx: (Math.random() - 0.5) * 0.2,
    vy: (Math.random() - 0.5) * 0.2,
    r: 0.8 + Math.random() * 1.8,
    color: THEME_COLORS[Math.floor(Math.random() * THEME_COLORS.length)],
    pulseSpeed: 0.3 + Math.random() * 0.7,
    pulsePhase: Math.random() * Math.PI * 2,
  };
}

function LayoutStarfield() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = window.innerWidth;
    let h = window.innerHeight;
    canvas.width = w;
    canvas.height = h;

    const stars: Star[] = [];
    for (let i = 0; i < STAR_COUNT; i++) stars.push(createStar(w, h));

    const handleResize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w;
      canvas.height = h;
    };
    window.addEventListener('resize', handleResize);

    let animId = 0;
    let lastFrame = 0;

    const draw = (timestamp: number) => {
      animId = requestAnimationFrame(draw);
      if (timestamp - lastFrame < FRAME_INTERVAL) return;
      lastFrame = timestamp;

      const t = timestamp / 1000;
      ctx.clearRect(0, 0, w, h);

      for (const s of stars) {
        s.x += s.vx;
        s.y += s.vy;
        if (s.x < -s.r) s.x = w + s.r;
        if (s.x > w + s.r) s.x = -s.r;
        if (s.y < -s.r) s.y = h + s.r;
        if (s.y > h + s.r) s.y = -s.r;
      }

      for (let i = 0; i < stars.length; i++) {
        for (let j = i + 1; j < stars.length; j++) {
          const dx = stars[i].x - stars[j].x;
          const dy = stars[i].y - stars[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            const alpha = (1 - dist / CONNECTION_DIST) * 0.25;
            ctx.beginPath();
            ctx.moveTo(stars[i].x, stars[i].y);
            ctx.lineTo(stars[j].x, stars[j].y);
            ctx.strokeStyle = `rgba(122,167,255,${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      for (const s of stars) {
        const pulseAlpha = Math.sin(t * s.pulseSpeed + s.pulsePhase) * 0.3 + 0.7;
        const [cr, cg, cb] = s.color;
        if (s.r > 1.5) {
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r * 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${cr},${cg},${cb},${pulseAlpha * 0.08})`;
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${cr},${cg},${cb},${pulseAlpha})`;
        ctx.fill();
      }
    };

    animId = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Breadcrumb
// ---------------------------------------------------------------------------

const BREADCRUMB_KEYS: Record<string, string> = {
  dashboard: 'breadcrumb.dashboard',
  customers: 'breadcrumb.customers',
  devices: 'breadcrumb.devices',
  engineers: 'breadcrumb.engineers',
  'admin-users': 'breadcrumb.adminUsers',
  invoices: 'breadcrumb.invoices',
  sites: 'breadcrumb.sites',
  monitor: 'breadcrumb.monitor',
  settings: 'breadcrumb.settings',
  commissioning: 'breadcrumb.commissioning',
  'setup-wizard': 'breadcrumb.setupWizard',
  camera: 'breadcrumb.camera',
  energy: 'breadcrumb.energy',
  learning: 'breadcrumb.learning',
  alerts: 'breadcrumb.alerts',
  circuits: 'breadcrumb.circuits',
  intelligence: 'breadcrumb.intelligence',
  'change-password': 'breadcrumb.changePassword',
  'setup-guide': 'breadcrumb.setupGuide',
  manual: 'breadcrumb.manual',
  tools: 'breadcrumb.tools',
  'hardware-calculator': 'breadcrumb.hwCalculator',
};

function Breadcrumb({ pathname }: { pathname: string }) {
  const { t } = useTranslation();
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length <= 1) return null; // No breadcrumb on top-level pages

  const crumbs: { label: string; path: string }[] = [];
  let accumulated = '';
  for (const seg of segments) {
    accumulated += '/' + seg;
    const key = BREADCRUMB_KEYS[seg];
    if (key) {
      crumbs.push({ label: t(key), path: accumulated });
    }
    // Skip UUID segments (site IDs, etc.)
  }
  if (crumbs.length <= 1) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      style={{
        padding: '8px 0 4px',
        fontSize: 12,
        color: '#5a6581',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
      }}
    >
      {crumbs.map((c, i) => (
        <React.Fragment key={c.path}>
          {i > 0 && <span style={{ opacity: 0.5 }}>/</span>}
          {i === crumbs.length - 1 ? (
            <span style={{ color: '#8892b0', fontWeight: 600 }}>{c.label}</span>
          ) : (
            <Link to={c.path} style={{ color: '#5a6581', textDecoration: 'none' }}>
              {c.label}
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const isPlatformOwner = !!user?.is_platform && user?.role === 'OWNER';
  const isOwner = user?.role === 'OWNER';
  const isEngineer = user?.role === 'ENGINEER';
  // SmartPitch HQ engineer (external contractor). Distinct from a
  // customer-org ENGINEER user, who wouldn't have is_platform=true.
  // Used to gate the site-config sidebar links (Setup Wizard, Site
  // Settings) that were previously OWNER-only but which the backend
  // was always designed to accept engineers for. Mirrors the
  // OwnerOrPlatformEngineerRoute check in App.tsx.
  const isPlatformEngineer = !!user?.is_platform && user?.role === 'ENGINEER';

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="sp-page-enter" style={{ display: 'flex', minHeight: '100vh', background: '#070b14' }}>
      {/* Background effects */}
      <div className="sp-layout-aurora" />
      <div className="sp-layout-drift" />
      <LayoutStarfield />
      <div className="sp-layout-orb sp-layout-orb-1" />
      <div className="sp-layout-orb sp-layout-orb-2" />
      <div className="sp-layout-scan" />
      <div className="sp-corner sp-corner-tl" />
      <div className="sp-corner sp-corner-tr" />
      <div className="sp-corner sp-corner-bl" />
      <div className="sp-corner sp-corner-br" />

      {/* Mobile hamburger button */}
      <button className="sp-hamburger" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label={t('nav.toggleMenu')}>
        {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      {/* Sidebar overlay (mobile) */}
      {sidebarOpen && <div className="sp-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <nav aria-label="Main navigation" className={`sp-sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sp-sidebar-logo">
          <img src="/logoforhtml.png" alt="SmartPitch" />
          {user && (
            <div className="sp-sidebar-user">
              {user.name || user.email}
              <span className="sp-role-badge">{user.role}</span>
            </div>
          )}
        </div>

        <div className="sp-sidebar-nav">
          {/* Main — platform staff (owners + engineers) */}
          {(isPlatformOwner || isEngineer) && (
            <>
              <div className="sp-nav-section">{t('nav.main')}</div>
              <NavLink
                to="/dashboard"
                label={t('nav.dashboard')}
                icon={<LayoutDashboard size={16} />}
                active={location.pathname === '/dashboard'}
              />
              <NavLink
                to="/customers"
                label={t('nav.customers')}
                icon={<Users size={16} />}
                active={location.pathname.startsWith('/customers')}
              />
              {isPlatformOwner && (
                <NavLink
                  to="/admin/devices"
                  icon={<Cpu size={16} />}
                  label={t('nav.deviceFleet')}
                  active={location.pathname === '/admin/devices'}
                />
              )}
            </>
          )}

          {/* Organisation — owners only (non-platform) */}
          {isOwner && !isPlatformOwner && (
            <>
              <div className="sp-nav-section">{t('nav.main')}</div>
              <NavLink
                to="/org/settings"
                label={t('nav.organisation')}
                icon={<Building2 size={16} />}
                active={location.pathname === '/org/settings'}
              />
            </>
          )}

          {/* Site navigation when viewing a site page */}
          {(() => {
            const siteMatch = location.pathname.match(/^\/sites\/([^/]+)/);
            if (!siteMatch) return null;
            const siteId = siteMatch[1];
            const p = location.pathname;
            return (
              <>
                <div className="sp-nav-section">{t('nav.site')}</div>
                <NavLink
                  to={`/sites/${siteId}/monitor`}
                  icon={<Monitor size={16} />}
                  label={t('nav.liveMonitor')}
                  active={false}
                />
                {(isPlatformOwner || isEngineer) && (
                  <>
                    <NavLink
                      to={`/sites/${siteId}/commissioning`}
                      icon={<Radio size={16} />}
                      label={t('nav.commissioning')}
                      active={p === `/sites/${siteId}/commissioning`}
                    />
                    <NavLink
                      to={`/sites/${siteId}/camera-commissioning`}
                      icon={<Camera size={16} />}
                      label={t('nav.cameraSetup')}
                      active={p === `/sites/${siteId}/camera-commissioning`}
                    />
                    <NavLink
                      to={`/sites/${siteId}/io`}
                      icon={<Cpu size={16} />}
                      label={t('nav.ioLive')}
                      active={p === `/sites/${siteId}/io`}
                    />
                  </>
                )}
                <NavLink
                  to={`/sites/${siteId}/schedule`}
                  icon={<Calendar size={16} />}
                  label={t('nav.schedule')}
                  active={p === `/sites/${siteId}/schedule`}
                />
                <NavLink
                  to={`/sites/${siteId}/alerts`}
                  icon={<Bell size={16} />}
                  label={t('nav.alerts')}
                  active={p === `/sites/${siteId}/alerts`}
                />
                <NavLink
                  to={`/sites/${siteId}/energy`}
                  icon={<Zap size={16} />}
                  label={t('nav.energy')}
                  active={p === `/sites/${siteId}/energy`}
                />
                {(isPlatformOwner || isOwner || isPlatformEngineer) && (
                  <>
                    <NavLink
                      to={`/sites/${siteId}/settings`}
                      icon={<Settings size={16} />}
                      label={t('nav.siteSettings')}
                      active={p === `/sites/${siteId}/settings`}
                    />
                    <NavLink
                      to={`/sites/${siteId}/setup`}
                      icon={<Wrench size={16} />}
                      label={t('nav.setupWizard')}
                      active={p === `/sites/${siteId}/setup`}
                    />
                  </>
                )}
              </>
            );
          })()}

          {/* Guides — visible to platform, owners, and engineers */}
          {(isPlatformOwner || isOwner || isEngineer) && (
            <>
              <div className="sp-nav-section">{t('nav.guides')}</div>
              <NavLink
                to="/setup-guide"
                label={t('nav.setupGuide')}
                icon={<ClipboardList size={16} />}
                active={location.pathname === '/setup-guide'}
              />
              <NavLink
                to="/manual"
                label={t('nav.manual')}
                icon={<FileText size={16} />}
                active={location.pathname === '/manual'}
              />
              {isPlatformOwner && (
                <NavLink
                  to="/onboarding-guide"
                  label={t('nav.onboarding')}
                  icon={<HardHat size={16} />}
                  active={location.pathname === '/onboarding-guide'}
                />
              )}
            </>
          )}

          {/* Tools — platform staff (owners + engineers) */}
          {(isPlatformOwner || isEngineer) && (
            <>
              <div className="sp-nav-section">{t('nav.tools')}</div>
              <NavLink
                to="/tools/hardware-calculator"
                label={t('nav.hwCalculator')}
                icon={<Calculator size={16} />}
                active={location.pathname === '/tools/hardware-calculator'}
              />
            </>
          )}

          {/* Billing — platform only */}
          {isPlatformOwner && (
            <>
              <div className="sp-nav-section">{t('nav.billing')}</div>
              <NavLink
                to="/invoices"
                icon={<Receipt size={16} />}
                label={t('nav.invoices')}
                active={location.pathname === '/invoices'}
              />
              <NavLink
                to="/invoices/settings"
                icon={<Settings size={16} />}
                label={t('nav.invoiceSettings')}
                active={location.pathname === '/invoices/settings'}
              />
            </>
          )}

          {/* Admin — platform only */}
          {isPlatformOwner && (
            <>
              <div className="sp-nav-section">{t('nav.admin')}</div>
              <NavLink
                to="/admin/users"
                icon={<Users size={16} />}
                label={t('nav.staffAccounts')}
                active={location.pathname === '/admin/users'}
              />
              <NavLink
                to="/admin/engineers"
                icon={<HardHat size={16} />}
                label={t('nav.engineers')}
                active={location.pathname === '/admin/engineers'}
              />
              <NavLink
                to="/admin/audit"
                icon={<ScrollText size={16} />}
                label={t('nav.auditTrail')}
                active={location.pathname === '/admin/audit'}
              />
            </>
          )}

          {/* SIM Management — platform only. Separate top-level section
              (per Joe's direction 2026-06-24) — room to add Plans /
              Usage tabs alongside Inventory in the future. */}
          {isPlatformOwner && (
            <>
              <div className="sp-nav-section">{t('nav.simManagement')}</div>
              <NavLink
                to="/admin/sims"
                icon={<Smartphone size={16} />}
                label={t('nav.simInventory')}
                active={location.pathname === '/admin/sims'}
              />
            </>
          )}
        </div>

        <div className="sp-sidebar-footer">
          {/* Language selector */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="sp-nav-link"
              style={{
                padding: '8px 12px',
                fontSize: 13,
                width: '100%',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Globe size={16} /> {SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)?.label || 'English'}
            </button>
            {langOpen && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  right: 0,
                  background: '#0d1525',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  overflow: 'hidden',
                  zIndex: 100,
                  boxShadow: '0 -8px 24px rgba(0,0,0,0.4)',
                }}
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      authApi.updateLanguage(lang.code).catch(() => {});
                      setLangOpen(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 16px',
                      background: i18n.language === lang.code ? 'rgba(46,229,157,0.1)' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: i18n.language === lang.code ? '#2ee59d' : '#8892b0',
                      fontSize: 13,
                      fontWeight: i18n.language === lang.code ? 600 : 400,
                    }}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Link to="/change-password" className="sp-nav-link" style={{ padding: '8px 12px', fontSize: 13 }}>
            <KeyRound size={16} /> {t('nav.changePassword')}
          </Link>
          <button onClick={handleLogout}>
            <LogOut size={16} /> {t('nav.signOut')}
          </button>
          <div style={{ fontSize: 10, color: '#5a6581', padding: '8px 12px 0', display: 'flex', gap: 8 }}>
            <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>
              {t('legal.privacy')}
            </Link>
            <span style={{ opacity: 0.4 }}>·</span>
            <Link to="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>
              {t('legal.terms')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main aria-label="Page content" className="sp-main-content">
        <Breadcrumb pathname={location.pathname} />
        {user?.billing_status === 'grace' && !user?.is_platform && (
          <div
            style={{
              background: 'rgba(255,107,74,0.12)',
              border: '1px solid rgba(255,107,74,0.4)',
              borderRadius: 8,
              padding: '10px 20px',
              margin: '0 0 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              fontSize: 13,
              fontWeight: 700,
              color: '#ff6b6b',
            }}
          >
            {t('subscriptionBanner.expired')}
          </div>
        )}
        {/* Phase 19m-C: per-route error boundary. Key on pathname +
            search so navigating to a different page — or just
            tweaking a query string on the same route — resets the
            error state and gives the next page a clean attempt. A
            crash in (say) SiteEnergy no longer blanks the sidebar
            or other routes. */}
        <RouteErrorBoundary key={location.pathname + location.search}>
          <Outlet />
        </RouteErrorBoundary>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nav link
// ---------------------------------------------------------------------------

function NavLink({ to, icon, label, active }: { to: string; icon: React.ReactNode; label: string; active?: boolean }) {
  return (
    <Link to={to} className={`sp-nav-link${active ? ' active' : ''}`}>
      {icon} {label}
    </Link>
  );
}
